/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { getTechSupportChatBaseUrl } from "@/utils/siteConfig";
import { safeSessionStorage } from "@/helper/safeLocalStorage";

export type HostPageTheme = "light" | "dark";

/**
 * Infer the visitor's theme from the host page so the embedded chat
 * matches. Deskshare marketing pages do not persist a theme class today,
 * so fall back to the operating-system preference.
 */
export function getHostPageTheme(): HostPageTheme {
  if (document.documentElement.classList.contains("dark")) {
    return "dark";
  }
  if (window.matchMedia?.("(prefers-color-scheme: dark)")?.matches) {
    return "dark";
  }
  return "light";
}

/**
 * The value that says "this visitor has chosen no product".
 *
 * Item 2 (Session 361). The website used to leave the product out of the
 * address entirely when nothing was selected, which says nothing at all — and
 * the chat cannot tell "the visitor chose nothing" from "the website did not
 * say". Faced with silence it falls back to the topic it stored from that
 * visitor's previous conversation, so somebody who once asked about Dictation
 * Pro was greeted about Dictation Pro on every later visit with nothing
 * selected on the page.
 *
 * The scope is therefore always stated, in a parameter of its own. A product
 * name is never used to mean the absence of a product: such a marker would be
 * indistinguishable from a real selection, would scope every general
 * conversation to that product in the chat's own records and reporting, and
 * would break silently the day the product it names is offered on the support
 * form. Every Deskshare product name is a real product somewhere in this
 * codebase, so there is no safe one to borrow.
 *
 * The chat service must be told to read this — one line on their side, and it
 * is the first entry in their list in the handoff.
 */
export const GENERAL_CHAT_SCOPE = "general";

/**
 * State the scope on a chat address: the product when one is chosen, the
 * general marker when none is.
 *
 * Shared by the frame's address and the widget's "open in a new tab" address,
 * which are built separately. Before this they could — and did — disagree.
 */
export function applyChatScope(
  params: URLSearchParams,
  productDisplayName?: string,
): void {
  const product = (productDisplayName ?? "").trim();
  if (product) {
    params.set("deskshare-product-name", product);
  } else {
    params.set("deskshare-scope", GENERAL_CHAT_SCOPE);
  }
}

/**
 * P-1 (Session 376): "the visitor pressed Close, so the next opening must start
 * a new conversation."
 *
 * Kept in per-tab storage, not in a React ref, because the thing it is about is
 * kept there too. The chat application holds the conversation in its own
 * per-tab storage on its own origin, and that survives a page change and a
 * reload. A ref does not: the widget is rendered on the product support page
 * only, so moving anywhere else on the site unmounts it and the flag is gone —
 * press Close, go to another page, come back, open the chat, and the previous
 * conversation returns. Storing the flag the same way the conversation is
 * stored is what makes the two live and die together.
 *
 * safeSessionStorage rather than a bare sessionStorage call: a browser set to
 * block site data throws on the bare call, and this runs inside a click handler
 * on the page, so the throw would take the page down. That wrapper already
 * solves it — writing a second try/catch here would duplicate a solved problem.
 */
const FRESH_CHAT_KEY = "ds_chat_fresh";

export const pendingFreshChat = {
  set(): void {
    safeSessionStorage.setItem(FRESH_CHAT_KEY, "1");
  },

  /** Read the flag and clear it, so it is honoured exactly once. */
  take(): boolean {
    const had = safeSessionStorage.getItem(FRESH_CHAT_KEY) === "1";
    safeSessionStorage.removeItem(FRESH_CHAT_KEY);
    return had;
  },
};

/**
 * Build the iframe URL for the chat widget. `embed=1` switches the chat
 * app to its compact layout; `theme` keeps it consistent with the host.
 *
 * Both parameters are optional and the widget is the only caller, so this is
 * backward compatible with every existing call.
 *
 * Do NOT add platform, project, or a legacy product parameter. The chat
 * service accepts `deskshare-product-name`, `deskshare-scope` and `lang`;
 * anything else is ignored at best and misread at worst.
 *
 * Two details that are deliberate and must not be "tidied":
 *
 *  - The language is truncated to its first two characters. An active locale
 *    can be a regional tag such as pt-BR, which the chat does not recognise.
 *  - The query is built with URLSearchParams rather than string
 *    concatenation. That is what makes a product name containing a space, an
 *    ampersand or an accent safe.
 *
 * One interoperability note, recorded in the handoff as work for the chat
 * team: URLSearchParams encodes a space as "+", so "Dictation Pro" is sent as
 * Dictation+Pro. Any standard query parser turns that back into a space; a
 * receiver that percent-decodes the raw text after the equals sign produces a
 * literal plus and silently fails to find the product.
 *
 * This module imports from siteConfig, so siteConfig must never import
 * from here — that would create a cycle.
 *
 * @param productDisplayName Canonical product name, or undefined when the
 *   visitor has selected nothing (or the catch-all, which is not a product).
 * @param language Active site language, e.g. "de" or "pt-BR".
 * @param freshSession True when the visitor closed the panel with the X since
 *   the last opening, so the chat must start a new conversation rather than
 *   restoring the one it has stored. The chat application must be told to read
 *   `deskshare-fresh` — it is in the handoff as work for that team, and until
 *   it lands this parameter changes nothing a visitor can see.
 */
export function buildTechSupportChatUrl(
  productDisplayName?: string,
  language?: string,
  freshSession = false,
  openHistory = false,
): string {
  const base = getTechSupportChatBaseUrl();
  const params = new URLSearchParams({ embed: "1", theme: getHostPageTheme() });

  // P-1: belt to the close message's braces. The message can be missed — the
  // chat may not have started, or the visitor may have reloaded the page since
  // — and this flag is the mechanism that still works in both cases, because it
  // is read from the address the chat is loaded with rather than from anything
  // either side has to remember.
  if (freshSession) {
    params.set("deskshare-fresh", "1");
  }

  // PH-1 (Session 447): ask the chat to open its past-conversations drawer.
  // Set only on the one journey that asks for it - a sign-in started from
  // inside the chat, returning to the conversation the customer was reading.
  //
  // This does nothing until the chat application reads it, and this site has
  // no way to detect that it was ignored: the panel would simply open on a
  // new conversation, which looks like the feature half working. So the
  // chat team's written confirmation is a deploy gate, exactly as it was for
  // deskshare-fresh=1 and for the same reason. Until then the website half
  // degrades honestly: the panel reopens, which is most of the value.
  if (openHistory) {
    params.set("deskshare-history", "1");
  }

  applyChatScope(params, productDisplayName);

  const lang = (language ?? "").slice(0, 2).toLowerCase();
  if (lang) {
    params.set("lang", lang);
  }

  return `${base}/?${params.toString()}`;
}
